import { nanoid } from '@reduxjs/toolkit';
import {
  BY_METRIC_LINE_WIDTH,
  ChartType,
  DEFAULT_LEFT_AXIS_SERIES,
  DEFAULT_LINE_WIDTH,
  DEFAULT_RIGHT_AXIS_SERIES,
  getRequestedMetrics,
} from '@/features/chartsView';
import type { AxisSeriesSettings } from '@/features/chartsView';
import type {
  Filters,
  NumberFilterParams,
  StringFilterParams,
} from '@/features/filters';
import { getApplicableFilters, prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type {
  DateRangeLimits,
  SerializedDateRangeValueObject,
} from '@/shared/types';
import { DateRangePresets, DateScale, Lifetime } from '@/shared/types';
import * as CohortHistoryChartAPI from '../api/cohortHistoryChartAPI';
import type { GetDateFilterParams } from '../api/types';
import type {
  CohortHistoryChartConfig,
  CohortHistoryChartDataResponse,
  ChartMeta,
} from './types';

export interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CohortHistoryChartConfig;
  isConfigInitialized: boolean;
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  isConfigInitialized: false,
  chart: {
    id: '',
    type: ChartType.cohortHistory,
    snapshotDateScale: DateScale.DAY,
    snapshotDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    cohortDateScale: DateScale.DAY,
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    lifetime: Lifetime.YEAR,
    probabilisticAttribution: true,
    splitDimension: '',
    primaryMetric: '',
    secondaryMetric: '',
    excludedSplitOptions: [],
    syncedYAxes: false,
    filters: [],
    leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
    rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
  },
};

const healAxisSeries = (
  series: AxisSeriesSettings | undefined,
  catalogMetrics: Set<string>,
  fallback: AxisSeriesSettings,
): AxisSeriesSettings => {
  const current = series ?? { ...fallback };

  if (current.lineWidthMetric && catalogMetrics.has(current.lineWidthMetric)) {
    return current;
  }

  if (!current.lineWidthMetric && current.lineWidth !== BY_METRIC_LINE_WIDTH) {
    return current;
  }

  return {
    ...current,
    lineWidthMetric: '',
    lineWidth:
      current.lineWidth === BY_METRIC_LINE_WIDTH
        ? DEFAULT_LINE_WIDTH
        : current.lineWidth,
  };
};

// The width metric is validated against the chart's whole metric catalog, not
// against the two plotted metrics: sizing lines by a metric that is not on an
// axis is the point of the setting. Only a metric the catalog does not know at
// all gets healed away.
const getHealedAxes = (
  chart: CohortHistoryChartConfig,
  catalogMetrics: Set<string>,
) => {
  return {
    leftAxisSeries: healAxisSeries(
      chart.leftAxisSeries,
      catalogMetrics,
      DEFAULT_LEFT_AXIS_SERIES,
    ),
    rightAxisSeries: healAxisSeries(
      chart.rightAxisSeries,
      catalogMetrics,
      DEFAULT_RIGHT_AXIS_SERIES,
    ),
  };
};

// Healing needs the catalog, and `updateChart` can land before meta resolves.
// Rather than validate against an empty catalog — which would wipe a setting
// the user legitimately picked — the heal is deferred: it is a no-op while meta
// is pending and `initMeta`'s fulfilled case runs it once the catalog arrives.
const healAxesAgainstMeta = (state: State) => {
  if (!isMetaFinished(state.meta)) {
    return;
  }

  // Assigned as a fresh object rather than mutated in place: `updateChart` can
  // receive a config that came out of the store or the RTK Query cache, which
  // Immer has deep-frozen. Immer keeps such a payload as the raw object rather
  // than drafting it, so writing through `state.chart` would hit the frozen
  // original and throw.
  state.chart = {
    ...state.chart,
    ...getHealedAxes(state.chart, new Set(state.meta.data.metrics)),
  };
};

export const cohortHistoryChartSlice = createAppSlice({
  name: 'cohortHistoryChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CohortHistoryChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
          // Runs the heal that any config applied before the catalog arrived
          // had to skip.
          healAxesAgainstMeta(state);
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.cohortHistoryChart.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
      },
    ),
    updateSnapshotDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.snapshotDateRange = action.payload;
      },
    ),
    updateCohortDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.cohortDateScale = action.payload;
    }),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.cohortDateRange = action.payload;
      },
    ),
    updateLifetime: create.reducer<number>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.lifetime = action.payload;
    }),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.splitDimension = action.payload;
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.primaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric]?.type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }

      healAxesAgainstMeta(state);
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.secondaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric]?.type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }

      healAxesAgainstMeta(state);
    }),
    updateAxisSeries: create.reducer<{
      axis: 'left' | 'right';
      patch: Partial<AxisSeriesSettings>;
    }>((state, action) => {
      if (!state.chart) {
        return;
      }

      const key =
        action.payload.axis === 'left' ? 'leftAxisSeries' : 'rightAxisSeries';

      state.chart[key] = { ...state.chart[key], ...action.payload.patch };
      healAxesAgainstMeta(state);
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CohortHistoryChartConfig>((state, action) => {
      state.chart = action.payload;
      state.isConfigInitialized = true;
      healAxesAgainstMeta(state);
    }),
    setupVendorDefault: create.reducer<{ lifetimeTwoYearsExtension: boolean }>(
      (state, action) => {
        if (!isMetaFinished(state.meta)) {
          return;
        }

        state.chart = {
          id: nanoid(),
          type: ChartType.cohortHistory,
          excludedSplitOptions: [],
          syncedYAxes: true,
          filters: [],
          snapshotDateScale: DateScale.DAY,
          snapshotDateRange: { preset: DateRangePresets.LAST_90_DAYS },
          cohortDateScale: DateScale.DAY,
          cohortDateRange: { preset: DateRangePresets.ALL_TIME },
          lifetime: action.payload.lifetimeTwoYearsExtension
            ? Lifetime.TWO_YEARS
            : Lifetime.YEAR,
          probabilisticAttribution: true,
          splitDimension: state.meta.data.defaultAxis.dimension,
          primaryMetric: state.meta.data.defaultAxis.primaryMetric,
          secondaryMetric: state.meta.data.defaultAxis.secondaryMetric,
          leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
          rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
        };
        state.isConfigInitialized = true;
      },
    ),
    setupVendorDefaultWithOverride: create.reducer<{
      lifetimeTwoYearsExtension: boolean;
      override: Partial<CohortHistoryChartConfig>;
    }>((state, action) => {
      if (!isMetaFinished(state.meta)) {
        return;
      }

      state.chart = {
        id: nanoid(),
        type: ChartType.cohortHistory,
        excludedSplitOptions: [],
        syncedYAxes: true,
        filters: [],
        snapshotDateScale: DateScale.DAY,
        snapshotDateRange: { preset: DateRangePresets.LAST_90_DAYS },
        cohortDateScale: DateScale.DAY,
        cohortDateRange: { preset: DateRangePresets.ALL_TIME },
        lifetime: action.payload.lifetimeTwoYearsExtension
          ? Lifetime.TWO_YEARS
          : Lifetime.YEAR,
        probabilisticAttribution: true,
        splitDimension: state.meta.data.defaultAxis.dimension,
        primaryMetric: state.meta.data.defaultAxis.primaryMetric,
        secondaryMetric: state.meta.data.defaultAxis.secondaryMetric,
        leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
        rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
        ...action.payload.override,
      };
      healAxesAgainstMeta(state);
      state.isConfigInitialized = true;
    }),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        const optionsToExclude = action.payload.slice(3);

        state.chart.excludedSplitOptions = optionsToExclude;
      },
    ),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      if (!state.chart) {
        return;
      }

      const excludedOptionsSet = new Set(state.chart.excludedSplitOptions);

      if (!action.payload.checked) {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.add(option);
        });
      } else {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.delete(option);
        });
      }

      state.chart.excludedSplitOptions = Array.from(excludedOptionsSet);
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectIsConfigInitialized: (state) => state.isConfigInitialized,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
  },
});

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export const getChartData =
  (params: CohortHistoryChartConfig): AppThunk<Promise<CohortHistoryChartDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState() as RootState;

    const { meta } = state.cohortHistoryChart;

    if (!isMetaFinished(meta)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { dimensionsFilters, metricsFilters } = prepareFilters(
      params.filters,
      meta.data.metrics,
      meta.data.dimensions,
    );

    return CohortHistoryChartAPI.getCohortHistoryChartData({
      split: params.splitDimension,
      metrics: getRequestedMetrics(params),
      snapshotDateScale: params.snapshotDateScale,
      snapshotDateRange: prepareDateRangeValueObject(params.snapshotDateRange),
      cohortDateScale: params.cohortDateScale,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      lifetime: params.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    });
  };

export const getDateFilterParams =
  (params: GetDateFilterParams): AppThunk<Promise<DateRangeLimits>> =>
  () => {
    return CohortHistoryChartAPI.getDateFilterParams(params).then((dto) => ({
      min: new Date(dto.min),
      max: new Date(dto.max),
    }));
  };

interface GetStringFilterParamsThunkParams {
  filterName: string;
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  snapshotDateRange: SerializedDateRangeValueObject;
  snapshotDateScale: DateScale;
  cohortDateRange: SerializedDateRangeValueObject;
  cohortDateScale: DateScale;
  probabilisticAttribution: boolean;
}

export const getStringFilterParams =
  (
    params: GetStringFilterParamsThunkParams,
  ): AppThunk<Promise<StringFilterParams>> =>
  () => {
    const { dimensionsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CohortHistoryChartAPI.getStringFilterParams({
      filterName: params.filterName,
      dimensionsFilters,
      snapshotDateRange: prepareDateRangeValueObject(params.snapshotDateRange),
      snapshotDateScale: params.snapshotDateScale,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      cohortDateScale: params.cohortDateScale,
      probabilisticAttribution: params.probabilisticAttribution,
    });
  };

interface GetNumberFilterParamsThunkParams {
  filterName: string;
  split: string;
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  snapshotDateRange: SerializedDateRangeValueObject;
  snapshotDateScale: DateScale;
  cohortDateRange: SerializedDateRangeValueObject;
  cohortDateScale: DateScale;
  lifetime: Lifetime;
  probabilisticAttribution: boolean;
}

export const getNumberFilterParams =
  (
    params: GetNumberFilterParamsThunkParams,
  ): AppThunk<Promise<NumberFilterParams>> =>
  () => {
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CohortHistoryChartAPI.getNumberFilterParams({
      filterName: params.filterName,
      split: params.split,
      dimensionsFilters,
      metricsFilters,
      snapshotDateRange: prepareDateRangeValueObject(params.snapshotDateRange),
      snapshotDateScale: params.snapshotDateScale,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      cohortDateScale: params.cohortDateScale,
      lifetime: params.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
    });
  };

export const {
  initMeta,
  updateSnapshotDateRange,
  updateCohortDateScale,
  updateCohortDateRange,
  updateLifetime,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateAxisSeries,
  updateFilters,
  updateChart,
  setupVendorDefault,
  setupVendorDefaultWithOverride,
  toggleExcludedSplitOptions,
  setupDefaultExcludedSplitOptions,
} = cohortHistoryChartSlice.actions;
export const {
  selectChart,
  selectIsConfigInitialized,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} = cohortHistoryChartSlice.selectors;
